import { AppWindow, Clock3, Repeat2, Star, UserRound } from "lucide-react";
import type { ProcessSummary } from "../../api/types";
import {
  type Categories,
  type GroupingKind,
  UNCLASSIFIED,
  categoryGroup,
  isOtherCategory,
  processCategoryId,
} from "./processGroups";
import { processPeople } from "./processPeople";

export type ProcessGrouping =
  | "none"
  | "domain"
  | "pattern"
  | "person"
  | "application"
  | "favourites"
  | "frequency"
  | "intensity";
export type PersonProcesses = Record<string, ProcessSummary[]>;

export const readProcessGrouping = (value: string | null): ProcessGrouping =>
  value === "0"
    ? "none"
    : value === "person" ||
        value === "application" ||
        value === "favourites" ||
        value === "frequency" ||
        value === "intensity" ||
        value === "pattern"
      ? value
      : "domain";

/** The classification a grouping groups by, if any. */
export const groupingKind = (grouping: ProcessGrouping): GroupingKind | undefined =>
  grouping === "domain"
    ? "business_domain"
    : grouping === "pattern"
      ? "process_pattern"
      : undefined;

// More than one run per three calendar days. Keep this divisor shared with the labels.
const FREQUENCY_DIVISOR = 3;
export const frequentMinimumRuns = (days: number) => Math.floor(days / FREQUENCY_DIVISOR) + 1;

/** Inclusive UTC calendar dates, matching the process directory's date filtering. */
export function processPeriodDays(processes: ProcessSummary[], from: string, to: string): number {
  const day = (value: string) => Date.parse(value.slice(0, 10));
  const starts = processes.map(process => day(process.firstSeen)).filter(Number.isFinite);
  const ends = processes.map(process => day(process.lastSeen)).filter(Number.isFinite);
  const start = from ? day(from) : Math.min(...starts);
  const end = to ? day(to) : Math.max(...ends);
  return Number.isFinite(start) && Number.isFinite(end)
    ? Math.max(1, Math.round((end - start) / 86_400_000) + 1)
    : 1;
}

export const processTotalDuration = (process: ProcessSummary) =>
  process.totalDuration ?? process.frequency * process.avgDuration;

// Rank by total observed time (cases × duration): where the team's time actually goes.
export const DEFAULT_PROCESS_SORT = "total";

/** The measure a grouping adds as its own column, sortable under `sort`. */
export const GROUPING_CONTEXT = {
  person: {
    column: "shareOfTeam",
    sort: "share",
    label: "process_directory.share_of_team",
    hint: "process_directory.share_of_team_hint",
    icon: UserRound,
  },
  application: {
    column: "timeInApp",
    sort: "app",
    label: "process_directory.time_in_app",
    hint: "process_directory.time_in_app_hint",
    icon: AppWindow,
  },
} as const;

export const groupingContext = (grouping: ProcessGrouping) =>
  grouping === "person" || grouping === "application" ? GROUPING_CONTEXT[grouping] : undefined;

/** Whether a sort applies to this view; one that does not falls back to the default. */
export const sortAvailable = (sort: string, grouping: ProcessGrouping, multiplePeople: boolean) =>
  sort === "shared"
    ? multiplePeople && grouping !== "person"
    : sort === "share" || sort === "app"
      ? groupingContext(grouping)?.sort === sort
      : true;

/** A person's share of the whole team's observed time on a process, when the team total is known. */
export const teamTimeShare = (process: ProcessSummary, team: ProcessSummary | undefined) => {
  const total = team ? processTotalDuration(team) : 0;
  return total > 0 ? processTotalDuration(process) / total : undefined;
};

export function compareProcesses(
  sort: string,
  multiplePeople: boolean,
  teamBySlug?: ReadonlyMap<string, ProcessSummary>,
) {
  const share = (process: ProcessSummary) =>
    teamTimeShare(process, teamBySlug?.get(process.slug)) ?? -1;
  return (a: ProcessSummary, b: ProcessSummary) =>
    (sort === "name"
      ? 0
      : sort === "duration"
        ? b.avgDuration - a.avgDuration
        : sort === "recent"
          ? Date.parse(b.lastSeen) - Date.parse(a.lastSeen)
          : sort === "shared" && multiplePeople
            ? processPeople(b).length - processPeople(a).length
            : sort === "share" && teamBySlug
              ? share(b) - share(a)
              : sort === "total"
                ? processTotalDuration(b) - processTotalDuration(a)
                : b.frequency - a.frequency) || a.name.localeCompare(b.name);
}

export type DirectoryGroup = {
  id: string;
  name: string;
  icon: typeof UserRound;
  tone: string;
  processes: ProcessSummary[];
  totalDuration: number | null;
  personId?: string;
  app?: string;
  bucket?:
    | "frequent"
    | "occasional"
    | "long"
    | "short"
    | "favourites"
    | "not_favourites"
    | "unclassified";
};

export function buildDirectoryGroups(
  processes: ProcessSummary[],
  grouping: ProcessGrouping,
  people: Array<{ id: string; label: string }>,
  personProcesses: PersonProcesses,
  sort: string,
  periodDays = 1,
  favouriteSlugs: ReadonlySet<string> = new Set(),
  categories: Categories = new Map(),
): DirectoryGroup[] {
  let groups: DirectoryGroup[];
  if (grouping === "favourites") {
    return (["favourites", "not_favourites"] as const)
      .map(bucket => {
        const starred = bucket === "favourites";
        const items = processes.filter(process => favouriteSlugs.has(process.slug) === starred);
        return {
          id: bucket,
          bucket,
          name: "",
          icon: Star,
          tone: starred
            ? "bg-amber-500/10 text-amber-700 dark:text-amber-300"
            : "bg-slate-500/10 text-text-mid",
          processes: items,
          totalDuration: items.reduce((sum, process) => sum + processTotalDuration(process), 0),
        };
      })
      .filter(group => group.processes.length > 0);
  }
  if (grouping === "frequency" || grouping === "intensity") {
    const definitions: Array<{
      bucket: NonNullable<DirectoryGroup["bucket"]>;
      matches: (process: ProcessSummary) => boolean;
      tone: string;
    }> =
      grouping === "frequency"
        ? [
            {
              bucket: "frequent",
              matches: process => process.frequency >= frequentMinimumRuns(periodDays),
              tone: "bg-blue-500/10 text-blue-700 dark:text-blue-300",
            },
            {
              bucket: "occasional",
              matches: process => process.frequency < frequentMinimumRuns(periodDays),
              tone: "bg-slate-500/10 text-text-mid",
            },
          ]
        : [
            {
              bucket: "long",
              matches: process => process.avgDuration >= 3600,
              tone: "bg-violet-500/10 text-violet-700 dark:text-violet-300",
            },
            {
              bucket: "short",
              matches: process => process.avgDuration < 3600,
              tone: "bg-cyan-500/10 text-cyan-700 dark:text-cyan-300",
            },
          ];
    // Bucket order is semantic; sorting only changes the processes inside each bucket.
    return definitions
      .map(({ bucket, matches, tone }) => {
        const items = processes.filter(matches);
        return {
          id: bucket,
          bucket,
          name: "",
          tone,
          icon: grouping === "frequency" ? Repeat2 : Clock3,
          processes: items,
          totalDuration: items.reduce((sum, process) => sum + processTotalDuration(process), 0),
        };
      })
      .filter(group => group.processes.length > 0);
  }
  if (grouping === "person") {
    groups = Object.entries(personProcesses).map(([id, items]) => ({
      id,
      personId: id,
      name: people.find(person => person.id === id)?.label ?? id,
      icon: UserRound,
      tone: "bg-blue-500/10 text-blue-700 dark:text-blue-300",
      processes: items,
      totalDuration: items.reduce((sum, process) => sum + processTotalDuration(process), 0),
    }));
  } else if (grouping === "application") {
    const apps = [...new Set(processes.flatMap(process => process.apps ?? []))];
    groups = apps.map(app => {
      const items = processes.filter(process => process.apps?.includes(app));
      const duration = (process: ProcessSummary) => process.appSeconds?.[app];
      if (sort === "app") {
        items.sort(
          (a, b) => (duration(b) ?? -1) - (duration(a) ?? -1) || a.name.localeCompare(b.name),
        );
      }
      return {
        id: app,
        app,
        name: app,
        icon: AppWindow,
        tone: "bg-cyan-500/10 text-cyan-700 dark:text-cyan-300",
        processes: items,
        // Missing attribution is unknown, never zero or the entire process duration.
        totalDuration: items.every(process => duration(process) !== undefined)
          ? items.reduce((sum, process) => sum + (duration(process) ?? 0), 0)
          : null,
      };
    });
    const unknown = processes.filter(process => !process.apps?.length);
    if (unknown.length)
      groups.push({
        id: "unavailable-applications",
        name: "",
        icon: AppWindow,
        tone: "bg-slate-500/10 text-text-mid",
        processes: unknown,
        totalDuration: null,
      });
  } else {
    const kind = groupingKind(grouping) ?? "business_domain";
    const ids = [...new Set(processes.map(process => processCategoryId(process, kind)))];
    groups = ids.map(id => {
      const items = processes.filter(process => processCategoryId(process, kind) === id);
      return {
        id,
        ...categoryGroup(id, categories),
        ...(id === UNCLASSIFIED && { bucket: "unclassified" as const }),
        processes: items,
        totalDuration: items.reduce((sum, process) => sum + processTotalDuration(process), 0),
      };
    });
  }
  // A kind's catch-all, then the unclassified, after the real categories.
  const rank = (group: DirectoryGroup) =>
    !groupingKind(grouping)
      ? 0
      : group.id === UNCLASSIFIED
        ? 2
        : isOtherCategory(group.name)
          ? 1
          : 0;
  return groups
    .filter(group => group.processes.length > 0)
    .sort(
      (a, b) =>
        rank(a) - rank(b) ||
        (b.totalDuration ?? -1) - (a.totalDuration ?? -1) ||
        a.name.localeCompare(b.name),
    );
}
